import React from 'react';
import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import {ICONS} from '../../lib/curo-icons.js';

/**
 * 칭찬(마일스톤) 토스트 — "됐다!" 순간에만 짧게 반응. 대표 아이디어(2026-07-24).
 *
 * ⚠️ 설계 원칙: 아이들은 깃발을 수십 번 누른다. 매번 칭찬하면 3분 만에 무시당하고 방해가 된다.
 *    그래서 **횟수가 아니라 "처음"**만 축하한다 — 마일스톤별로 프로젝트당 1회(localStorage 기록).
 *    과장 금지(중등1~2 대상이라 유치하면 역효과), 2.6초 후 자동 소멸, 클릭 방해 없음(pointer-events: none).
 *
 * 자리: 자동저장 토스트(좌하단)와 겹치지 않게 **하단 중앙**.
 */

const KEY = 'curoai_cheer_done';

const MSG = {
    firstRun: {en: 'It works! First run 🎉', ko: '첫 실행 성공! 🎉', vi: 'Chạy được rồi! 🎉'},
    blocks10: {en: '10 blocks! Nice work', ko: '블록 10개 돌파! 잘하고 있어요', vi: '10 khối rồi! Giỏi lắm'},
    pyOk: {en: 'Python ran with no errors!', ko: '파이썬 실행 성공! 에러 하나도 없어요', vi: 'Python chạy không lỗi!'},
    aiTrained: {en: 'Your AI finished learning! 🧠', ko: 'AI 학습 완료! 🧠 이제 알아볼 수 있어요', vi: 'AI đã học xong! 🧠'},
    aiPredict: {en: 'Your AI recognized it! 🎯', ko: 'AI가 알아봤어요! 🎯', vi: 'AI đã nhận ra! 🎯'}
};
const MSG_EXTRA = {
    ja: {firstRun: 'はじめての実行、成功！🎉', blocks10: 'ブロック10個とっぱ！いい調子', pyOk: 'Python実行せいこう！エラーなし', aiTrained: 'AIの学習が完了！🧠', aiPredict: 'AIが見わけた！🎯'},
    'zh-cn': {firstRun: '第一次运行成功！🎉', blocks10: '积木满10个！做得好', pyOk: 'Python 运行成功，没有错误！', aiTrained: 'AI 训练完成！🧠', aiPredict: 'AI 认出来了！🎯'},
    'zh-tw': {firstRun: '第一次執行成功！🎉', blocks10: '積木滿10個！做得好', pyOk: 'Python 執行成功，沒有錯誤！', aiTrained: 'AI 訓練完成！🧠', aiPredict: 'AI 認出來了！🎯'},
    de: {firstRun: 'Es läuft! Erster Start 🎉', blocks10: '10 Blöcke! Gut gemacht', pyOk: 'Python lief ohne Fehler!', aiTrained: 'Deine KI hat gelernt! 🧠', aiPredict: 'Deine KI hat es erkannt! 🎯'},
    es: {firstRun: '¡Funciona! Primera ejecución 🎉', blocks10: '¡10 bloques! Buen trabajo', pyOk: '¡Python se ejecutó sin errores!', aiTrained: '¡Tu IA terminó de aprender! 🧠', aiPredict: '¡Tu IA lo reconoció! 🎯'},
    fr: {firstRun: 'Ça marche ! Premier lancement 🎉', blocks10: '10 blocs ! Bien joué', pyOk: 'Python a tourné sans erreur !', aiTrained: 'Ton IA a fini d\'apprendre ! 🧠', aiPredict: 'Ton IA l\'a reconnu ! 🎯'},
    it: {firstRun: 'Funziona! Prima esecuzione 🎉', blocks10: '10 blocchi! Ottimo lavoro', pyOk: 'Python eseguito senza errori!', aiTrained: 'La tua AI ha imparato! 🧠', aiPredict: 'La tua AI l\'ha riconosciuto! 🎯'},
    ru: {firstRun: 'Работает! Первый запуск 🎉', blocks10: '10 блоков! Молодец', pyOk: 'Python отработал без ошибок!', aiTrained: 'Твой ИИ обучился! 🧠', aiPredict: 'Твой ИИ узнал! 🎯'}
};

// 이미 축하한 마일스톤인지 (프로젝트 단위가 아니라 브라우저 단위 — 단순하게)
const done = key => {
    try { return (JSON.parse(localStorage.getItem(KEY) || '[]') || []).indexOf(key) >= 0; } catch (e) { return true; }
};
const markDone = key => {
    try {
        const a = JSON.parse(localStorage.getItem(KEY) || '[]') || [];
        if (a.indexOf(key) < 0) a.push(key);
        localStorage.setItem(KEY, JSON.stringify(a));
    } catch (e) { /* noop */ }
};

/** 다른 곳(파이썬 탭 등)에서 축하를 띄울 때 쓰는 헬퍼 */
export const cheer = key => {
    try { window.dispatchEvent(new CustomEvent('curoai-cheer', {detail: {key}})); } catch (e) { /* noop */ }
};

class Cheer extends React.Component {
    constructor (props) {
        super(props);
        this.state = {msg: null};
        this._onRunStop = this._onRunStop.bind(this);
        this._onEvent = this._onEvent.bind(this);
        this._ranOnce = false;
    }
    componentDidMount () {
        window.addEventListener('curoai-cheer', this._onEvent);
        const vm = this.props.vm;
        if (!vm) return;
        // 실행이 "끝났을 때" 축하 — 시작 시점에 띄우면 아직 성공인지 모른다
        try { vm.on('PROJECT_RUN_STOP', this._onRunStop); } catch (e) { /* noop */ }
    }
    componentWillUnmount () {
        window.removeEventListener('curoai-cheer', this._onEvent);
        try { this.props.vm.off('PROJECT_RUN_STOP', this._onRunStop); } catch (e) { /* noop */ }
        clearTimeout(this._t);
    }
    _blockCount () {
        let n = 0;
        try {
            (this.props.vm.runtime.targets || []).forEach(t => {
                if (!t || t.isOriginal === false) return;
                const bl = t.blocks && t.blocks._blocks;
                if (!bl) return;
                Object.keys(bl).forEach(id => { if (bl[id] && !bl[id].shadow) n++; });
            });
        } catch (e) { /* noop */ }
        return n;
    }
    _onRunStop () {
        // 첫 실행 성공
        if (!this._ranOnce) {
            this._ranOnce = true;
            if (!done('firstRun')) { this._show('firstRun'); return; }
        }
        // 블록 10개 돌파 (실행 끝난 시점에만 확인 — 만드는 중엔 방해 안 함)
        if (!done('blocks10') && this._blockCount() >= 10) this._show('blocks10');
    }
    _onEvent (ev) {
        const key = ev && ev.detail && ev.detail.key;
        if (key && !done(key)) this._show(key);
    }
    _show (key) {
        markDone(key);
        this.setState({msg: this.t(key)});
        clearTimeout(this._t);
        this._t = setTimeout(() => this.setState({msg: null}), 2600);
    }
    t (key) {
        let loc = (this.props.intl && this.props.intl.locale) || 'en';
        if (loc === 'ja-Hira') loc = 'ja';
        const ex = MSG_EXTRA[loc];
        if (ex && ex[key] != null) return ex[key];
        const m = MSG[key];
        if (!m) return '';
        return m[loc] || m.en || '';
    }
    render () {
        if (!this.state.msg) return null;
        return (
            <div style={S.wrap}>
                <div style={S.toast}>
                    <span style={S.icon} dangerouslySetInnerHTML={{__html: ICONS.spark}} />
                    {this.state.msg}
                </div>
            </div>
        );
    }
}

const S = {
    // pointer-events: none — 축하가 클릭을 가로채지 않게(교실에서 결정적)
    wrap: {position: 'fixed', left: 0, right: 0, bottom: 26, zIndex: 9100,
        display: 'flex', justifyContent: 'center', pointerEvents: 'none'},
    toast: {background: '#1f2540', color: '#fff', borderRadius: 20, padding: '9px 18px',
        fontSize: 14, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 7,
        boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif'},
    icon: {display: 'inline-flex', fontSize: 17, color: '#ffd75e'}
};

Cheer.propTypes = {
    intl: PropTypes.object,
    vm: PropTypes.object
};

export default injectIntl(Cheer);
